import { useEffect, useState } from "react";
import { Wrench, StopCircle, Plus } from "lucide-react";
import type { MaintenanceWindow } from "../types";
import { useEndMaintenance, useExtendMaintenance } from "../hooks/useMaintenanceStatus";

interface Props {
  active: MaintenanceWindow;
}

function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${m}min ${s}s`;
  if (m > 0) return `${m}min ${s}s`;
  return `${s}s`;
}

export function MaintenanceActiveBar({ active }: Props) {
  const [now, setNow] = useState(() => Date.now());
  const endMutation = useEndMaintenance();
  const extendMutation = useExtendMaintenance();

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const endsAt = new Date(active.ends_at).getTime();
  const msLeft = endsAt - now;

  return (
    <div className="w-full bg-gray-900 text-gray-100 text-xs font-mono py-1.5 px-4 flex items-center gap-2 z-50 flex-shrink-0">
      <Wrench size={13} className="flex-shrink-0 text-gray-400" />
      <span className="text-gray-400">[DEV]</span>
      <span className="font-semibold">Przerwa techniczna trwa:</span>
      <span className="truncate">{active.title}</span>
      <span className="ml-auto text-gray-300 whitespace-nowrap">
        {msLeft > 0 ? `koniec za ${formatCountdown(msLeft)}` : "kończy się…"}
      </span>
      <button
        onClick={() => extendMutation.mutate(active.id)}
        disabled={extendMutation.isPending}
        className="ml-2 flex items-center gap-1 text-yellow-400 hover:text-yellow-300 disabled:opacity-50 whitespace-nowrap transition-colors"
        aria-label="Przedłuż o 15 min"
      >
        <Plus size={13} />
        {extendMutation.isPending ? "…" : "+15 min"}
      </button>
      <button
        onClick={() => endMutation.mutate(active.id)}
        disabled={endMutation.isPending}
        className="ml-2 flex items-center gap-1 text-red-400 hover:text-red-300 disabled:opacity-50 whitespace-nowrap transition-colors"
        aria-label="Zakończ teraz"
      >
        <StopCircle size={13} />
        {endMutation.isPending ? "…" : "Zakończ teraz"}
      </button>
    </div>
  );
}
